<script setup lang="ts">
import { computed, Ref, ref, watch, onMounted } from 'vue';
import RexSelectV3 from '@/rex-components/RexSelectV3.vue';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import {
  SelectFieldOption,
  useGetTeamsData,
} from '@/components/Main/Tenant/useGetTeamsData';
import { useRouter, useRoute } from 'vue-router';
import { RexSelectOption } from '@/rex-components/rex.comp.config';
import { useStore } from 'vuex';
import debounce from 'lodash/debounce';
import { SELECT_FIELD_DEBOUNCE_DURATION_IN_MS } from '@/utils/constants';
import { AuthService } from '@/services/api/auth.service';
import { notificationError } from '@/utils/notification';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const setTeamIdMutation = (teamId) =>
  store.commit('globalState/user/setTeamId', teamId);
const setTeamNameMutation = (teamName) =>
  store.commit('globalState/user/setTeamName', teamName);
const setPrevTeamDataMutation = (teamData) =>
  store.commit('globalState/user/setPrevTeamData', teamData);
const getTeamName = computed(
  () => store.getters['globalState/user/getTeamName'],
);
const getTeamId = computed(() => store.getters['globalState/user/getTeamId']);
const getPrevTeamData = computed(
  () => store.getters['globalState/user/getPrevTeamData'],
);

const options: Ref<Array<SelectFieldOption>> = ref([]);
const pageNo = ref(1);
const currTeamName = ref('');
const searchTeam = ref('');
const { data, fetchTeamsList, isLoading } = useGetTeamsData();
fetchTeamsList(pageNo.value, searchTeam.value);

async function onOptionSelect(value, option) {
  setPrevTeamDataMutation({
    teamId: getTeamId.value,
    teamName: currTeamName.value,
  });
  currTeamName.value = option.value;
  setTeamIdMutation(option.key.split('_')[0]);
  setTeamNameMutation(value);
  router.push({ name: 'loadingPage', query: { updateAuth: 'true' } });
  if (searchTeam.value) {
    searchTeam.value = '';
    pageNo.value = 1;
    await fetchTeamsList(pageNo.value, searchTeam.value);
  }
}

function onScrollOptions(event) {
  const target = event.target;
  if (
    !isLoading.value &&
    target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
    options.value?.length < data.value.total
  ) {
    pageNo.value++;
    fetchTeamsList(pageNo.value, searchTeam.value);
  }
}

function onOpenSelectField(open) {
  if (open && searchTeam.value) {
    searchTeam.value = '';
    pageNo.value = 1;
    fetchTeamsList(pageNo.value, searchTeam.value);
  }
}

const onSearchTeams = debounce(function (val) {
  options.value = [];
  searchTeam.value = val;
  pageNo.value = 1;
  fetchTeamsList(pageNo.value, searchTeam.value);
}, SELECT_FIELD_DEBOUNCE_DURATION_IN_MS);

watch(data, async (newVal) => {
  if (options.value.length === 0 && (!newVal?.teams || newVal?.total === 0)) {
    notificationError(t('no-tenant-err-msg'));
    AuthService.makeLogout();
    return;
  } else if (
    !currTeamName.value &&
    !getTeamName.value &&
    newVal.teams?.length > 0
  ) {
    currTeamName.value = newVal.teams[0].label;
    setTeamIdMutation(newVal.teams[0].value);
    setTeamNameMutation(newVal.teams[0].label);
    router.push({
      name: 'loadingPage',
      query: { updateAuth: 'true', redirectPath: route.path },
    });
  }
  options.value =
    pageNo.value === 1
      ? [...newVal.teams]
      : [...options.value, ...newVal.teams];
});
watch(
  () => route.query.updateTeamData,
  (newVal) => {
    if (newVal === 'false') {
      currTeamName.value = getPrevTeamData.value.teamName;
      setTeamIdMutation(getPrevTeamData.value.teamId);
      setTeamNameMutation(getPrevTeamData.value.teamName);
    }
  },
  { immediate: true },
);
watch(
  () => route.query.updateTeamList,
  async (newVal) => {
    if (newVal === 'true') {
      setTeamIdMutation('');
      setTeamNameMutation('');
      router.replace({ query: {} });
      options.value = [];
      pageNo.value = 1;
      searchTeam.value = '';
      currTeamName.value = '';
      await fetchTeamsList(pageNo.value, searchTeam.value);
    }
  },
  { immediate: true },
);
onMounted(() => {
  if (getTeamName?.value) {
    currTeamName.value = getTeamName.value;
  }
});
</script>

<template>
  <div>
    <RexSelectV3
      :showSearch="true"
      @onPopupScroll="onScrollOptions"
      @onSearch="onSearchTeams"
      :value="currTeamName"
      @onChange="onOptionSelect"
      id="app-side-bar-select-team-id-field"
      :customStyle="{ width: '19rem' }"
      @onDropdownVisibleChange="onOpenSelectField"
    >
      <template #default>
        <RexSelectOption
          v-for="(obj, i) in options"
          :key="`${obj.value}_${i}`"
          :value="obj.label"
        >
          <RexTooltipV3
            :title="obj.label"
            placement="right"
            class="sidebar-team-id-opt-tooltip"
          >
            <div class="sidebar-team-id-opt-tooltip-text">
              {{ obj.label }}
            </div>
          </RexTooltipV3>
        </RexSelectOption>
        <RexSelectOption
          :disabled="true"
          v-if="isLoading"
          key="loading"
          :value="searchTeam"
        >
          <div v-if="isLoading" class="sidebar-team-id-loading-box">
            <RexSpinV3 size="small" />
          </div>
        </RexSelectOption>
      </template>
    </RexSelectV3>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-team-id-loading-box {
  @include flex-center;
  height: 100%;
  width: 100%;
}
.sidebar-team-id-opt-tooltip {
  overflow: hidden;
  white-space: nowrap;
}
.sidebar-team-id-opt-tooltip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
